import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  InputLabel,
  FormControl,
  Collapse,
  Tabs,
  Tab,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../utils/api';
import { orderedCategoriesFor } from '../templates/templateTokens';
import Dialog from '../common/Dialog';
import Field from '../common/Field';
import SourceChoices from '../intake/SourceChoices';
import { tokens } from '../../theme';

// Intake is the way into the print intake. It is index 2 — appended, not
// inserted — because 0 and 1 are compared against directly below (the
// cover_only type, the template_id, where to navigate). Its tab shows the three
// ways in: two continue on /intake, and "Haven't started writing yet" switches
// to the New Manuscript tab. Nothing under it can create a draft: the form and
// the Create button are not rendered, and handleSubmit refuses it as well.
const INTAKE_TAB = 2;

// One row in the template list: a radio dot, the name, and the section count.
// A plain button rather than MUI's Radio because the row itself is the control
// — the whole strip is the hit target, which is what makes a long list of these
// usable on a phone.
const TemplateOption = ({ label, meta, selected, onClick, rowRef }) => (
  <Box
    ref={rowRef}
    component="button"
    type="button"
    onClick={onClick}
    data-testid="template-option"
    data-selected={selected ? 'true' : 'false'}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      width: '100%',
      textAlign: 'left',
      p: '9px 10px',
      borderRadius: '5px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.8rem',
      fontFamily: 'inherit',
      background: selected ? 'rgba(212,175,55,0.22)' : 'transparent',
      color: selected ? '#FFFFFF' : 'rgba(255,255,255,0.8)',
      '&:hover': { background: selected ? 'rgba(212,175,55,0.28)' : 'rgba(255,255,255,0.08)' },
    }}
  >
    <Box
      component="span"
      sx={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        flexShrink: 0,
        position: 'relative',
        border: `1.5px solid ${selected ? '#D4AF37' : 'rgba(255,255,255,0.4)'}`,
        ...(selected && {
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '2.5px',
            borderRadius: '50%',
            background: '#D4AF37',
          },
        }),
      }}
    />
    <Box component="span" sx={{ flex: 1 }}>{label}</Box>
    {meta && (
      <Box component="span" sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)' }}>
        {meta}
      </Box>
    )}
  </Box>
);

// `initialTemplateId` is how the Template Gallery hands over its choice. It is
// optional and defaults to null, so EditorHome — which has rendered this modal
// on its own for as long as it has existed — is unchanged by templates
// existing: it passes nothing, the section opens collapsed on "Blank", and the
// POST carries no template_id.
const NewDocumentModal = ({ open, onClose, initialTemplateId = null }) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Manuscript, 1 = Book Cover, 2 = Intake
  const [formData, setFormData] = useState({
    genre: '',
    title: '',
    authorName: '',
    volume: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [showCollaborators, setShowCollaborators] = useState(false);

  // --- Template selection ---
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplateId);
  // Open when the gallery sent us here with a choice, so the author can see
  // what they picked without hunting for it; collapsed otherwise, which is how
  // the modal has always opened from EditorHome.
  const [showTemplates, setShowTemplates] = useState(Boolean(initialTemplateId));

  // NEW: invite collaborators (by username)
  const [inviteQuery, setInviteQuery] = useState('');
  const [inviteResults, setInviteResults] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);

  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // "Haven't started writing yet" stays in the popup on New Manuscript; the
  // other two leave it for the print intake, carrying the choice along. Not
  // through onClose: on /editor-home closing the popup navigates to /dashboard,
  // which put an extra page in the history and sent the intake's Back there.
  // Leaving the route unmounts the popup with the page that rendered it.
  const handleIntakeChoice = (source) => {
    if (source === 'new') {
      setActiveTab(0);
      return;
    }
    navigate(`/intake?source=${source}`);
  };

  // Re-sync on every open, not just on mount. The gallery keeps one instance of
  // this modal and changes `initialTemplateId` as the author clicks different
  // cards, so state seeded only at mount would show the first template they
  // ever picked for the rest of the session.
  useEffect(() => {
    if (!open) return;
    setSelectedTemplateId(initialTemplateId);
    setShowTemplates(Boolean(initialTemplateId));
  }, [open, initialTemplateId]);

  // The catalogue, fetched when the dialog is first opened rather than on
  // mount — EditorHome renders this modal on every visit, and the list is only
  // needed once someone is actually looking at it.
  useEffect(() => {
    if (!open || templates.length > 0) return;

    let cancelled = false;

    const loadTemplates = async () => {
      try {
        const accessToken = localStorage.getItem('rv_access_token');
        const res = await fetch(`${API_BASE}/api/manuscript-templates`, {
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        // A missing catalogue costs the author the template section, not the
        // ability to create a draft — so this is logged, not surfaced.
        console.error('Failed to load templates:', err);
      }
    };

    loadTemplates();
    return () => { cancelled = true; };
  }, [open, templates.length]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || null;

  // Bring the chosen template into view when the list opens.
  //
  // The list is scrollable and grouped by category, so a template from a
  // category low down — a screenplay, a planning sheet — sits below the fold.
  // Without this the author arrives from that card, opens the picker, and sees
  // the fiction group with every radio empty: the summary line says one thing
  // and the visible list appears to say nothing is chosen.
  const selectedRowRef = useRef(null);
  useEffect(() => {
    if (!showTemplates || !selectedTemplateId) return;
    // Optional-chained because jsdom does not implement scrollIntoView, and a
    // component test should not have to stub a scroll to render a list.
    selectedRowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [showTemplates, selectedTemplateId, templates.length]);

  // --- Invite collaborators logic ---
  const handleInviteQueryChange = (e) => {
    setInviteQuery(e.target.value);
  };

  useEffect(() => {
    if (!inviteQuery || inviteQuery.trim().length < 2) {
      setInviteResults([]);
      return;
    }

    let cancelled = false;

    const runSearch = async () => {
      try {
        const accessToken = localStorage.getItem('rv_access_token');
        const res = await fetch(
          `${API_BASE}/api/users/search?q=${encodeURIComponent(
            inviteQuery.trim()
          )}`,
          {
            credentials: 'omit',
            headers: {
              Accept: 'application/json',
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
          }
        );

        if (!res.ok) {
          if (!cancelled) setInviteResults([]);
          return;
        }

        const data = await res.json();
        if (!cancelled) {
          setInviteResults(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Invite search failed:', err);
          setInviteResults([]);
        }
      }
    };

    const timeoutId = setTimeout(runSearch, 250);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [inviteQuery]);

  const handleAddInvite = (user) => {
    if (!user) return;
    setInvitedUsers((prev) => {
      if (prev.find((u) => u.id === user.id)) return prev;
      return [...prev, user];
    });
  };

  const handleRemoveInvite = (userId) => {
    setInvitedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleSubmit = async () => {
    // prevent double-clicks; Intake has nothing to create yet
    if (submitting || activeTab === INTAKE_TAB) return;

    setSubmitting(true);
    try {
      const isBookCover = activeTab === 1;

      const payload = {
        title: formData.title,
        genre: formData.genre,
        authorName: formData.authorName,
        volume: formData.volume,
        ...(isBookCover ? { type: 'cover_only' } : {}),
        // A cover has no manuscript body to seed, so the template goes nowhere
        // on that tab — the section is hidden there and the id is dropped here
        // rather than sent and ignored.
        ...(!isBookCover && selectedTemplateId ? { template_id: selectedTemplateId } : {}),
      };

      const accessToken = localStorage.getItem('rv_access_token');

      const response = await fetch(`${API_BASE}/api/drafts`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error('Create draft failed:', response.status, response.statusText);
        throw new Error('Failed to create draft');
      }

      const newDraft = await response.json();
      const draftId = newDraft.id || newDraft._id;

      if (!draftId) {
        throw new Error('Draft created but no id returned');
      }

      // NEW: add invited collaborators
      if (invitedUsers.length > 0) {
        const accessToken = localStorage.getItem('rv_access_token');
        for (const u of invitedUsers) {
          try {
            await fetch(`${API_BASE}/api/drafts/${draftId}/collaborators`, {
              method: 'POST',
              credentials: 'omit',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              },
              body: JSON.stringify({ user_id: u.id }),
            });
          } catch (err) {
            console.error('Failed to add collaborator', u, err);
          }
        }
      }

      // close modal and navigate to appropriate page
      if (onClose) onClose();
      if (activeTab === 1) {
        // Book Cover → go to cover designer
        navigate(`/editor/${draftId}/cover`);
      } else {
        // Manuscript → go to editor
        navigate(`/editor/${draftId}`);
      }
    } catch (error) {
      console.error('Error creating draft:', error);
      alert('Could not create draft. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create"
      tone="dark"
      width={560}
      // A stray Escape used to close this and, on /editor-home, navigate away
      // — taking the title and everything else with it, silently.
      dirty={Boolean(formData.title)}
      discardMessage="Discard this draft? What you have entered will be lost."
      actions={
        <>
          {/* Variants, not sx. The theme owns what secondary looks like in
              every state — including disabled, which is what made the old
              hand-styled Create button invisible on this purple panel. */}
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          {/* Hidden rather than disabled under Intake: a disabled Create reads
              as "fill something in and it will work", and there is nothing to
              fill in. */}
          {activeTab !== INTAKE_TAB && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={submitting || !formData.title}
              sx={{ boxShadow: 'none' }}
            >
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          )}
        </>
      }
    >
      {/* No positioning class here any more: the shared Dialog owns the panel,
          the centring and the padding. `.new-document-modal` used to do all
          three and fought the Dialog for them. */}
      <Box>

        {/* Tab toggle: New Manuscript / New Book Cover / Intake */}
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            mt: 2,
            mb: 1,
            '& .MuiTabs-indicator': { backgroundColor: tokens.color.goldFlat },
            '& .MuiTab-root': {
              color: '#ccc',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: { xs: '0.85rem', sm: '1rem' },
              flex: 1,
              // MUI's 90px floor makes three tabs 270px, wider than the row on
              // a 360px phone; the row then scrolls and clips "New Manuscript".
              minWidth: 0,
              px: { xs: 1, sm: 2 },
            },
            '& .Mui-selected': {
              color: '#FFFFFF !important',
              backgroundColor: alpha(tokens.color.goldFlat, 0.25),
            },
            backgroundColor: 'rgba(255,255,255,0.08)',
            borderRadius: 1,
          }}
        >
          <Tab label="New Manuscript" />
          <Tab label="New Book Cover" />
          <Tab label="Intake" />
        </Tabs>

        {activeTab === INTAKE_TAB && (
          <Box sx={{ mt: 2, mb: 1 }} data-testid="intake-tab">
            <Typography sx={{ mb: 1.5, color: tokens.color.onDark, fontSize: '0.9rem' }}>
              Get your book printed and bound. Where is it coming from?
            </Typography>
            <SourceChoices tone="dark" onChoose={handleIntakeChoice} />
          </Box>
        )}

        {/* The create form, down to the closing </> below. Deliberately not
            re-indented under the wrapper: lint:tokens reads every added line,
            and re-indenting would re-add the raw colours already in here. */}
        {activeTab !== INTAKE_TAB && (
        <>
        {/* Was a solid #D4AF37 block with dark ink — the only input of its
            kind in the product, and the first thing anyone sees when starting
            a manuscript. The shared Field handles the dark ground instead. */}
        <Field
          tone="dark"
          margin="normal"
          name="title"
          label="Title"
          value={formData.title}
          onChange={handleChange}
          required
          helperText="A working title is enough — you can change it later."
        />

        {/* Template picker. Manuscript tab only — see the payload note above.
            Colours are this file's existing hardcoded palette rather than
            theme tokens: src/CLAUDE.md says match the pattern the file already
            uses, and converting the whole modal is its own cleanup. */}
        {activeTab === 0 && templates.length > 0 && (
          <>
            <Button
              onClick={() => setShowTemplates((prev) => !prev)}
              endIcon={showTemplates ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              data-testid="template-section-toggle"
              sx={{
                mt: 1,
                color: '#F5D76E',
                textTransform: 'none',
                fontSize: '0.85rem',
                p: 0,
                '&:hover': { background: 'transparent', color: '#fff' },
              }}
            >
              {selectedTemplate ? `Template: ${selectedTemplate.name}` : 'Choose a template'}
            </Button>

            <Collapse in={showTemplates}>
              <Box sx={{ mt: 1, maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
                {/* Blank first: starting with no structure is the default the
                    modal had before templates existed, not a fallback. */}
                <TemplateOption
                  label="Blank (no template)"
                  selected={selectedTemplateId === null}
                  onClick={() => setSelectedTemplateId(null)}
                />

                {orderedCategoriesFor(templates).map((cat) => (
                  <Box key={cat.id}>
                    <Typography
                      sx={{
                        fontSize: '0.6rem',
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.45)',
                        mt: 1.5,
                        mb: 0.5,
                      }}
                    >
                      {cat.label}
                    </Typography>
                    {templates
                      .filter((t) => t.category === cat.id)
                      .map((t) => (
                        <TemplateOption
                          key={t.id}
                          label={t.name}
                          meta={`${(t.sections || []).length} sections`}
                          selected={selectedTemplateId === t.id}
                          rowRef={selectedTemplateId === t.id ? selectedRowRef : undefined}
                          onClick={() => setSelectedTemplateId(t.id)}
                        />
                      ))}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </>
        )}

        {/* Optional details toggle */}
        <Button
          onClick={() => setShowOptional((prev) => !prev)}
          endIcon={showOptional ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            mt: 1,
            color: '#F5D76E',
            textTransform: 'none',
            fontSize: '0.85rem',
            p: 0,
            '&:hover': { background: 'transparent', color: '#fff' },
          }}
        >
          {showOptional ? 'Hide optional details' : 'Add optional details'}
        </Button>

        <Collapse in={showOptional}>
          <Box>
            <FormControl
              fullWidth
              margin="normal"
              sx={{
                '& .MuiInputLabel-root': { color: '#1A1A1A' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#D4AF37',
                  color: '#1A1A1A',
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1A1A1A' },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1A1A1A' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1A1A1A' },
                '& .MuiSelect-icon': { color: '#1A1A1A' },
              }}
            >
              <InputLabel>Genre</InputLabel>
              <Select name="genre" value={formData.genre} onChange={handleChange}>
                <MenuItem value=""><em style={{ color: '#888' }}>— None —</em></MenuItem>
                <MenuItem value="fiction">Fiction</MenuItem>
                <MenuItem value="non-fiction">Non-Fiction</MenuItem>
                <MenuItem value="academic">Academic</MenuItem>
              </Select>
            </FormControl>

            <Field
              tone="dark"
              margin="normal"
              name="authorName"
              label="Author Name"
              value={formData.authorName}
              onChange={handleChange}
            />

            <FormControl
              fullWidth
              margin="normal"
              sx={{
                '& .MuiInputLabel-root': { color: '#1A1A1A' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#D4AF37',
                  color: '#1A1A1A',
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1A1A1A' },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1A1A1A' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1A1A1A' },
                '& .MuiSelect-icon': { color: '#1A1A1A' },
              }}
            >
              <InputLabel>Volume</InputLabel>
              <Select name="volume" value={formData.volume} onChange={handleChange} label="Volume">
                <MenuItem value=""><em style={{ color: '#888' }}>— None —</em></MenuItem>
                {[...Array(20)].map((_, i) => (
                  <MenuItem key={i + 1} value={(i + 1).toString()}>{i + 1}</MenuItem>
                ))}
              </Select>
            </FormControl>

          </Box>
        </Collapse>

        {/* Invite collaborators — separate collapsible section */}
        <Button
          onClick={() => setShowCollaborators((prev) => !prev)}
          endIcon={showCollaborators ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            mt: 1,
            color: '#F5D76E',
            textTransform: 'none',
            fontSize: '0.85rem',
            p: 0,
            '&:hover': { background: 'transparent', color: '#fff' },
          }}
        >
          {showCollaborators ? 'Hide collaborators' : 'Invite collaborators'}
        </Button>

        <Collapse in={showCollaborators}>
          <Box>
            <Field
              tone="dark"
              margin="dense"
              placeholder="Search by username"
              value={inviteQuery}
              onChange={handleInviteQueryChange}
            />

            {inviteQuery.trim().length >= 2 && inviteResults.length > 0 && (
              <Box sx={{ mt: 1, maxHeight: 150, overflowY: 'auto' }}>
                {inviteResults
                  .filter((u) => !invitedUsers.some((sel) => sel.id === u.id))
                  .map((u) => (
                    <Button
                      key={u.id}
                      onClick={() => handleAddInvite(u)}
                      sx={{
                        display: 'block',
                        width: '100%',
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        mb: 0.5,
                        backgroundColor: '#F5D76E',
                        color: '#1A1A1A',
                        '&:hover': { backgroundColor: '#f7e18a' },
                      }}
                    >
                      {u.username}
                    </Button>
                  ))}
              </Box>
            )}

            {invitedUsers.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {invitedUsers.map((u) => (
                  <Button
                    key={u.id}
                    variant="outlined"
                    size="small"
                    onClick={() => handleRemoveInvite(u.id)}
                    sx={{
                      borderColor: '#F5D76E',
                      color: '#F5D76E',
                      textTransform: 'none',
                    }}
                  >
                    {u.username} ✕
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
        </>
        )}

      </Box>
    </Dialog>
  );
};

export default NewDocumentModal;
